import React, { useState } from "react";
import { Typography, Input, Toastify, toast, Icon } from "top-brains-ui";
import styles from "@src/pages/login/change-password/ChangePasswordModal.module.scss";
import { useNavigate } from "react-router-dom";
import { useChangePasswordMutation } from "@src/api/LoginApiSlice";
import GradiantButton from "@src/components/gradiant-btn/GradiantButton";
import { useSelector } from "react-redux";
import CommonLoader from "@src/components/loader/CommonLoader";
import {
  COMMON_COLORS,
  ICON_COLOR_CONSTANTS,
} from "@src/styles/ColorConstants";
import { RootState } from "@src/app/store";
import { PASSWORD_VALIDATION } from "@src/constants/UiConstants";

const ChangePasswordModal: React.FC = () => {
  const navigate = useNavigate();
  const userId = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.id
  );

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);
  const [isValidLength, setIsValidLength] = useState(false);

  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [showPasswordValidations, setShowPasswordValidations] = useState(false);

  const handleNewPasswordChange = (value: string) => {
    setHasLowercase(/[a-z]/.test(value));
    setHasUppercase(/[A-Z]/.test(value));
    setHasNumber(/\d/.test(value));
    setHasSpecialChar(/[!@#$%^&*(),.?":{}|<>]/.test(value));
    setIsValidLength(value.length >= 8 && value.length <= 20);
  };

  const validate = (
    label: string,
    name: string,
    value: string,
    related?: string
  ) => {
    let error = "";

    if (!value) {
      error = `${label} is required`;
    } else {
      if (name === "newPassword" && value === oldPassword) {
        error = "New password must be different from old password";
      }
      if (name === "confirmPassword" && value !== related) {
        error = "Passwords must match";
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const isFormValid =
    oldPassword &&
    newPassword &&
    confirmPassword &&
    Object.values(errors).every((e) => !e);

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    try {
      await changePassword({
        payload: {
          userId,
          oldPassword,
          newPassword,
        },
      }).unwrap();

      toast.success("Password changed successfully");

      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err: any) {
      toast.error(err?.data?.response || "Something went wrong");
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.cardContainer}>
          <div className={styles.card}>
            <Typography
              fontSize={24}
              fontWeight="semi-bold"
              className={styles.title}
            >
              Change Password
            </Typography>
            <Input
              name="Old Password"
              type="password"
              label="Old Password"
              value={oldPassword}
              required
              variant="website"
              onChange={(e) => {
                setOldPassword(e.target.value);
                validate("Old Password", "oldPassword", e.target.value);
              }}
              error={Boolean(errors.oldPassword)}
              helperText={errors.oldPassword}
            />
            <div className={styles.formErrorField}>
              <div className={styles.passwordFieldWrapper}>
                <Input
                  name="New Password"
                  type={showNewPassword ? "text" : "password"}
                  label="New Password"
                  value={newPassword}
                  required
                  variant="website"
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    handleNewPasswordChange(e.target.value);
                    validate("New Password", "newPassword", e.target.value);

                    setShowPasswordValidations(e.target.value.length > 0);
                  }}
                  onFocus={() => {
                    setIsPasswordFocused(true);
                    if (newPassword.length > 0)
                      setShowPasswordValidations(true);
                  }}
                  onBlur={() => {
                    setIsPasswordFocused(false);
                    setShowPasswordValidations(false);
                  }}
                  error={Boolean(errors.newPassword)}
                  helperText={errors.newPassword}
                />

                <span
                  className={styles.passwordIcon}
                  onClick={() => setShowNewPassword((prev) => !prev)}
                >
                  <Icon
                    name={
                      showNewPassword ? "view_access_icon" : "hide_access_icon"
                    }
                    color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                  />
                </span>
              </div>

              {(isPasswordFocused || showPasswordValidations) && (
                <div className={styles.passwordValidation}>
                  <div className={styles.validationColumn}>
                    <p
                      className={isValidLength ? styles.valid : styles.invalid}
                    >
                      {isValidLength ? "✓" : "✗"}{" "}
                      {PASSWORD_VALIDATION.CHARACTER_LIMIT}
                    </p>
                    <p className={hasUppercase ? styles.valid : styles.invalid}>
                      {hasUppercase ? "✓" : "✗"} {PASSWORD_VALIDATION.UPPERCASE}
                    </p>
                    <p
                      className={hasSpecialChar ? styles.valid : styles.invalid}
                    >
                      {hasSpecialChar ? "✓" : "✗"}{" "}
                      {PASSWORD_VALIDATION.SPECIAL_CHARACTER}
                    </p>
                  </div>

                  <div className={styles.validationColumn}>
                    <p className={hasLowercase ? styles.valid : styles.invalid}>
                      {hasLowercase ? "✓" : "✗"}{" "}
                      {PASSWORD_VALIDATION.LOWER_CASE}
                    </p>
                    <p className={hasNumber ? styles.valid : styles.invalid}>
                      {hasNumber ? "✓" : "✗"} {PASSWORD_VALIDATION.NUMBER}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.passwordFieldWrapper}>
              <Input
                name="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                label="Confirm Password"
                value={confirmPassword}
                required
                variant="website"
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  validate(
                    "Confirm Password",
                    "confirmPassword",
                    e.target.value,
                    newPassword
                  );
                }}
                error={Boolean(errors.confirmPassword)}
                helperText={errors.confirmPassword}
              />

              <span
                className={styles.passwordIcon}
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                <Icon
                  name={
                    showConfirmPassword
                      ? "view_access_icon"
                      : "hide_access_icon"
                  }
                  color={ICON_COLOR_CONSTANTS.FILTER_COLOR_ICON}
                />
              </span>
            </div>
          </div>

          <div className={styles.actionBtns}>
            <GradiantButton
              variant="secondary"
              label="Cancel"
              onClick={() => navigate(-1)}
              width="192px"
              height="44px"
            />
            <GradiantButton
              variant="primary"
              label="Confirm"
              onClick={handleSubmit}
              disabled={!isFormValid}
              width="192px"
              height="44px"
            />
          </div>
        </div>

        {isLoading && (
          <CommonLoader
            color={COMMON_COLORS.WEBSITE_GREEN_LIGHT}
            overLayColor={COMMON_COLORS.LOADER_OVERLAY}
          />
        )}
      </div>

      <Toastify />
    </>
  );
};

export default ChangePasswordModal;
